/**
 * TruePrice V1 - Size Constants
 * 
 * Anchor points for the size multiplier decay curve.
 * Source: Addendum D (Decay Curve), Addendum E (Extended to 10 sq in)
 */

/**
 * Size anchor points for interpolation
 * Source: Addendum E Section 1.1
 * 
 * The curve DECAYS as size increases (volume discount).
 * Micro works get massive boost to cover minimum effort.
 */
export const SIZE_ANCHORS = [
  { area: 10, multiplier: 4.0 },     // Micro cap (was 10.0× — overshoot fix)
  { area: 50, multiplier: 2.5 },     // Small work midpoint (smooths descent to 100)
  { area: 100, multiplier: 1.5 },    // Small work premium
  { area: 500, multiplier: 1.0 },    // Market anchor (Medium)
  { area: 1500, multiplier: 0.7 },   // Volume discount (Large)
] as const;

/**
 * Thresholds for special handling
 */
export const SIZE_THRESHOLDS = {
  /** Below this: clamp to maximum multiplier */
  MICRO_MIN: 10,
  
  /** Below this: disable max density cap (P-GLOBAL-05) */
  SMALL_WORK: 144,  // 12×12
  
  /** Below this: apply minimum floor protection (P-GLOBAL-04) */
  MINIMUM_FLOOR: 25,  // ~5×5
  
  /** Below this: apply detail premium taper end */
  DETAIL_PREMIUM_MAX: 500,
  
  /** Below this: apply full detail premium */
  DETAIL_PREMIUM_FULL: 200,
  
  /** Above this: clamp to minimum multiplier */
  LARGE_MAX: 1500,
} as const;

/**
 * Interpolate size multiplier from area
 * Source: Addendum E Section 1.1
 * 
 * @param area - Area in square inches
 * @returns Size multiplier (S_size)
 */
export function interpolateSizeMultiplier(area: number): number {
  // Clamp to bounds
  if (area <= SIZE_ANCHORS[0].area) {
    return SIZE_ANCHORS[0].multiplier; // 10.0
  }
  if (area >= SIZE_ANCHORS[SIZE_ANCHORS.length - 1].area) {
    return SIZE_ANCHORS[SIZE_ANCHORS.length - 1].multiplier; // 0.7
  }
  
  // Find surrounding anchors
  for (let i = 0; i < SIZE_ANCHORS.length - 1; i++) {
    const lower = SIZE_ANCHORS[i];
    const upper = SIZE_ANCHORS[i + 1];
    
    if (area >= lower.area && area <= upper.area) {
      // Linear interpolation
      const ratio = (area - lower.area) / (upper.area - lower.area);
      return lower.multiplier + ratio * (upper.multiplier - lower.multiplier);
    }
  }
  
  // Fallback (should never reach)
  return 1.0;
}

/**
 * Get detail premium multiplier for L4+ small works
 * Source: Addendum E Section 3.3
 * 
 * @param area - Area in square inches
 * @param isHighTier - True if tier >= L4
 * @returns Detail premium multiplier (1.0–1.5)
 */
export function getDetailPremium(area: number, isHighTier: boolean): number {
  if (!isHighTier) return 1.0;
  if (area >= SIZE_THRESHOLDS.DETAIL_PREMIUM_MAX) return 1.0;
  if (area < SIZE_THRESHOLDS.DETAIL_PREMIUM_FULL) return 1.5;
  
  // Taper from 1.5 to 1.0 between 200 and 500
  const ratio = (area - SIZE_THRESHOLDS.DETAIL_PREMIUM_FULL) / 
                (SIZE_THRESHOLDS.DETAIL_PREMIUM_MAX - SIZE_THRESHOLDS.DETAIL_PREMIUM_FULL);
  return 1.5 + ratio * (1.0 - 1.5);
}
